import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Nav, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faFileInvoiceDollar, faMoneyBillAlt, faPercent } from '@fortawesome/free-solid-svg-icons';
import { currencyFormat } from 'helpers/utils';
import { TransactionDetailsProps, ViewType, OrderTotals } from './types';
import { FilterTabItem } from 'components/common/FilterTab';
import { ProductItem } from 'components/transactions/types';
import SummaryCard from './SummaryCard';
import OrdersView from './OrdersView';
import ProductsView from './ProductsView';
import { useTransactionSummary } from './hooks/useTransactionSummary';
import { useTableColumns } from './hooks/useTableColumns';
import { useColumnVisibility } from './hooks/useColumnVisibility';

/**
 * TransactionDetails Component - Displays details of a transaction including orders and products
 * Refactored to use smaller, focused components and custom hooks
 * Uses order_totals from API if available, otherwise calculates from orders
 */
const TransactionDetails: React.FC<TransactionDetailsProps> = ({
  orders,
  products = [],
  productsLoading = false,
  productsError = null,
  transactionDate,
  transactionId,
  orderTotals
}) => {
  const { t } = useTranslation();
  // State
  const [viewType, setViewType] = useState<ViewType>('products'); // Start with products view for testing
  const [activeTab, setActiveTab] = useState<string>('all');
  
  // Create a custom function for handling tab changes
  const handleViewTypeChange = (key: string) => {
    setViewType(key as ViewType);
  };
  
  // Debugging
  console.log("TransactionDetails rendering with ID:", transactionId);
  
  // Custom hooks
  const { orderColumns, productColumns } = useTableColumns();
  
  const { 
    visibleColumns,
    columnOptions,
    handleToggleColumn,
    orderColumnVisibility,
    productColumnVisibility,
    setOrderColumnVisibility,
    setProductColumnVisibility
  } = useColumnVisibility({
    orderColumns,
    productColumns,
    viewType
  });
  
  // Event handlers
  const handleTabClick = useCallback((tabValue: string) => {
    setActiveTab(tabValue);
  }, []);
  
  // Define tab items
  const tabItems: FilterTabItem[] = useMemo(() => [
    // {
    //   label: 'All Orders',
    //   value: 'all',
    //   count: orders.length,
    //   onClick: () => handleTabClick('all')
    // }
  ], [orders.length, handleTabClick]);
  
  // Get totals from orderTotals (which now correctly gets calculated from products if not provided by API)
  // or fall back to calculating from orders only as a last resort
  const { totalAmount, totalCommission, totalNetEarnings } = useMemo(() => {
    if (orderTotals && 
        orderTotals.total_amount !== undefined && 
        orderTotals.total_commission !== undefined &&
        orderTotals.total_transaction !== undefined) {
      console.log("Using order totals from products data:", orderTotals);
      return {
        totalAmount: orderTotals.total_amount,
        totalCommission: orderTotals.total_commission,
        totalNetEarnings: orderTotals.total_transaction
      };
    }
    // Fallback to calculated values from orders if orderTotals not provided or incomplete
    console.log("Falling back to calculated values from orders");
    return useTransactionSummary(orders);
  }, [orderTotals, orders]);

  // Summary card components - ensure they display even with no transactions
  const summaryCards = useMemo(() => [
    {
      title: t("transactions.detail.cards.transactionDate", "Transaction Date"),
      value: transactionDate || '-',
      icon: <FontAwesomeIcon icon={faCalendarAlt} className="text-warning opacity-75" />
    },
    {
      title: t("transactions.detail.cards.totalAmount", "Total Amount"),
      value: currencyFormat(totalAmount || 0),
      icon: <FontAwesomeIcon icon={faFileInvoiceDollar} className="text-warning opacity-75" />
    },
    {
      title: t("transactions.detail.cards.totalCommission", "Total Commission"),
      value: currencyFormat(totalCommission || 0),
      icon: <FontAwesomeIcon icon={faPercent} className="text-warning opacity-75" />
    },
    {
      title: t("transactions.detail.cards.totalTransaction", "Total Transaction"),
      value: currencyFormat(totalNetEarnings || 0),
      icon: <FontAwesomeIcon icon={faMoneyBillAlt} className="text-warning opacity-75" />
    }
  ], [t, transactionDate, totalAmount, totalCommission, totalNetEarnings]);
  
  return (
    <div className="mb-9">
      {/* Summary Cards */}
      <Row className="g-3 mb-4">
        {summaryCards.map((card, index) => (
          <Col xs={12} sm={6} md={3} key={`summary-card-${index}`}>
            <SummaryCard 
              title={card.title} 
              value={card.value}
              icon={card.icon}
            />
          </Col>
        ))}
      </Row>
      
      {/* View Toggle */}
      <div className="mb-4">
        <style>
          {`
            /* Sliding tab styles with flexbox - no absolute positioning */
            .tab-container {
              display: flex;
              flex-direction: column;
              width: 100%;
              border-bottom: 1px solid #dee2e6;
              position: relative;
            }
            
            .tabs-wrapper {
              display: flex;
              width: 100%;
            }
            
            .tab-item {
              flex: 1;
              display: flex;
              justify-content: center;
              align-items: center;
              position: relative;
            }
            
            .tab-button {
              background: none;
              border: none;
              padding: 1rem 0;
              color: #5e6e82;
              font-size: 0.95rem;
              cursor: pointer;
              transition: color 0.3s ease;
              text-align: center;
              width: 100%;
            }
            
            .tab-button:hover {
              color: #f5803e;
            }
            
            .tab-button.active {
              color: #f5803e;
              font-weight: 500;
            }
            
            .tab-indicator-container {
              display: flex;
              width: 100%;
              position: relative;
            }
            
            .tab-indicator-placeholder {
              flex: 1;
              display: flex;
              justify-content: center;
            }
            
            .tab-indicator {
              width: 80px;
              height: 3px;
              background: linear-gradient(to right, #f5803e, #ffa563);
              border-radius: 3px 3px 0 0;
              box-shadow: 0 0 8px rgba(245, 128, 62, 0.7);
              transition: transform 0.3s ease;
              position: relative;
              bottom: 0;
            }
          `}
        </style>
        
        <div className="tab-container">
          <div className="tabs-wrapper">
            <div className="tab-item">
              <button 
                className={`tab-button ${viewType === 'orders' ? 'active' : ''}`}
                onClick={() => handleViewTypeChange('orders')}
              >
                {t("transactions.detail.views.orderView", "Order View")}
              </button>
            </div>
            <div className="tab-item">
              <button 
                className={`tab-button ${viewType === 'products' ? 'active' : ''}`}
                onClick={() => handleViewTypeChange('products')}
              >
                {t("transactions.detail.views.productView", "Product View")}
              </button>
            </div>
          </div>
          
          <div className="tab-indicator-container">
            <div className="tab-indicator-placeholder">
              {viewType === 'orders' && <div className="tab-indicator" />}
            </div>
            <div className="tab-indicator-placeholder">
              {viewType === 'products' && <div className="tab-indicator" />}
            </div>
          </div>
        </div>
      </div>
      
      {/* Both views are rendered but only one is visible at a time */}
      <div className="transaction-views-container">
        <div style={{ display: viewType === 'orders' ? 'block' : 'none' }}>
          <OrdersView 
            transactionId={transactionId}
            orders={orders}
            columns={visibleColumns}
            columnOptions={columnOptions}
            onToggleColumn={handleToggleColumn}
            tabItems={tabItems}
            onTabClick={handleTabClick}
          />
        </div>
        
        <div style={{ display: viewType === 'products' ? 'block' : 'none' }}>
          <ProductsView 
            transactionId={transactionId}
            products={products}
            loading={productsLoading}
            error={productsError}
            columns={visibleColumns}
            columnOptions={columnOptions}
            onToggleColumn={handleToggleColumn}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(TransactionDetails);
